import axios from "axios";
import configData from "../../config.json";

// Create centralized Axios instance
const apiClient = axios.create({
  baseURL: configData.API_ENDPOINT,
  timeout: 12000, // 12s timeout to prevent hanging sockets
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor: automatically attach Authorization token if available
apiClient.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("token");
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Calculate Exponential Backoff with Full Jitter delay (in ms)
 * Formula: random_between(min_floor, min(max_cap, base * 2 ^ attempt))
 */
const getFullJitterDelay = (attempt, base = 1000, max = 6000) => {
  const exponentialCap = Math.min(max, base * Math.pow(2, attempt));
  // Full Jitter: randomize delay between 300ms and exponentialCap
  const minFloor = 300;
  return Math.floor(Math.random() * (exponentialCap - minFloor + 1)) + minFloor;
};

/**
 * Helper to wait until browser comes back online if currently offline
 */
const waitForOnline = (maxWaitMs = 15000) => {
  if (typeof window === "undefined" || typeof navigator === "undefined" || navigator.onLine) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let timer;
    const handleOnline = () => {
      window.removeEventListener("online", handleOnline);
      if (timer) clearTimeout(timer);
      resolve();
    };

    timer = setTimeout(() => {
      window.removeEventListener("online", handleOnline);
      resolve();
    }, maxWaitMs);

    window.addEventListener("online", handleOnline);
  });
};

// Response interceptor: Exponential Backoff + Full Jitter retry logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    // If no config or retry is explicitly disabled, fail immediately
    if (!config || config.noRetry) {
      return Promise.reject(error);
    }

    config.__retryCount = config.__retryCount || 0;
    const maxRetries = config.maxRetries !== undefined ? config.maxRetries : 2; // default: 2 retries (3 total attempts)

    // Only retry on:
    // 1. Network errors (no response, connection reset, DNS failure)
    // 2. Request timeouts (ECONNABORTED)
    // 3. Transient server errors (502, 503, 504)
    const isNetworkOrTimeout = !error.response || error.code === "ECONNABORTED" || error.message?.includes("timeout");
    const isTransientServer = error.response && (error.response.status === 502 || error.response.status === 503 || error.response.status === 504);

    const shouldRetry = (isNetworkOrTimeout || isTransientServer) && config.__retryCount < maxRetries;

    if (!shouldRetry) {
      return Promise.reject(error);
    }

    config.__retryCount += 1;

    // If offline, wait for network to recover before consuming retry
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      await waitForOnline(15000);
    }

    // Calculate Full Jitter delay for this attempt
    const delay = getFullJitterDelay(config.__retryCount - 1);
    console.warn(
      `[API Retry] Request to ${config.url} failed. Retrying (attempt ${config.__retryCount}/${maxRetries}) in ${delay}ms with Full Jitter...`
    );

    await new Promise((resolve) => setTimeout(resolve, delay));

    // Re-execute request with current config
    return apiClient(config);
  }
);

export default apiClient;
